import { CSSProperties, FC, ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { MenuProps } from 'antd';
import { Badge, Button, Dropdown, Popconfirm, Space, Typography } from 'antd';
import { TbCheck, TbCircleCheck, TbClock, TbClockPlay, TbPlayerPause, TbPlus, TbSettings, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    DiagramCanvasSurface,
    DiagramNodeSurface,
    DiagramViewport,
    DiagramZoomControls,
} from '@/shared/components/diagrams';
import { PreferenceModalIconAvatar } from '@/shared/components/modals/PreferenceModalPrimitives';
import SmallTag from '@/shared/components/tags/SmallTag';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { AlertSchedule } from '@/types';

import { AlertRequiredField } from '../../requiredFields';
import { AlertFlowNodeIcon } from '../AlertFlowNodeInspector';
import type { AlertFlowNodeDetail } from '../AlertFlowNodeInspector';
import AlertRequiredFieldsPreviewPopover from '../AlertRequiredFieldsPreviewPopover';

interface AlertRuntime {
    currentStep: 'delivery' | 'evaluation' | 'paused' | 'scheduled';
    evaluationCount: number;
}

interface AlertFlowPreviewProps {
    categoryLabel: string;
    conditionLabels: string[];
    conditionExpansion?: AlertFlowExpansionConfig;
    conditionRequiredFields?: AlertRequiredField[][];
    deliveryLabels: string[];
    deliveryExpansion?: AlertFlowExpansionConfig;
    deliveryRequiredFields?: AlertRequiredField[][];
    description?: string;
    inspectorOpen?: boolean;
    match: 'all' | 'any';
    missingRequiredFields?: AlertRequiredField[];
    name?: string;
    onRemoveCondition?: (index: number) => void;
    onRemoveDelivery?: (index: number) => void;
    onInspectorOpenChange?: (open: boolean) => void;
    onNodeSelect?: (node: AlertFlowNodeDetail) => void;
    onSelectRequiredField?: (field: AlertRequiredField) => void;
    runtime?: AlertRuntime;
    schedule: AlertSchedule;
    selectedNodeId?: string;
}

interface AlertFlowExpansionOption {
    description: string;
    icon: ReactNode;
    key: string;
    label: string;
    onSelect: () => void;
    requiresConfiguration?: boolean;
}

export interface AlertFlowExpansionConfig {
    ariaLabel: string;
    menuLabel?: string;
    options: AlertFlowExpansionOption[];
}

interface AlertFlowFitInput {
    contentHeight: number;
    contentWidth: number;
    viewportHeight: number;
    viewportWidth: number;
}

interface FlowNodeProps {
    active?: boolean;
    detail: AlertFlowNodeDetail;
    onSelect: () => void;
    onSelectRequiredField?: (field: AlertRequiredField) => void;
    removeAction?: {
        ariaLabel: string;
        confirmDescription: string;
        confirmTitle: string;
        disabled?: boolean;
        disabledReason?: string;
        onRemove: () => void;
    };
    selected?: boolean;
    status?: FlowStatus;
    width?: CSSProperties['width'];
}

interface ParallelFlowGroupProps {
    entryExpansion?: AlertFlowExpansionConfig;
    mergesIntoHandle?: boolean;
    nodes: ReactNode[];
}

type FlowStatus = 'Completed' | 'Configured' | 'Paused' | 'Ready' | 'Running' | 'Scheduled' | 'Waiting';

const parallelNodeWidth = 15;
const parallelNodeGap = 1.5;
const connectorHeight = 56;
const graphInset = 32;

const statusIcons: Record<FlowStatus, ReactNode> = {
    Completed: <TbCircleCheck size='0.75rem' />,
    Configured: <TbSettings size='0.75rem' />,
    Paused: <TbPlayerPause size='0.75rem' />,
    Ready: <TbCheck size='0.75rem' />,
    Running: <TbClockPlay size='0.75rem' />,
    Scheduled: <TbClock size='0.75rem' />,
    Waiting: <TbClock size='0.75rem' />,
};

const frequencyLabels: Record<AlertSchedule['frequency'], string> = {
    day: 'Daily',
    month: 'Monthly',
    nineMonths: 'Every nine months',
    sixMonths: 'Every six months',
    threeMonths: 'Quarterly',
    twoWeeks: 'Every two weeks',
    week: 'Weekly',
    weekday: 'Every weekday',
    year: 'Yearly',
};

const clamp = (value: number, minimum: number, maximum: number) => Math.min(maximum, Math.max(minimum, value));

export const calculateAlertFlowFitScale = ({
    contentHeight,
    contentWidth,
    viewportHeight,
    viewportWidth,
}: AlertFlowFitInput): number => {
    if (!contentHeight || !contentWidth || !viewportHeight || !viewportWidth) {
        return 1;
    }

    const horizontalScale = (viewportWidth - graphInset * 2) / contentWidth;
    const verticalScale = (viewportHeight - graphInset * 2) / contentHeight;

    return clamp(Math.min(horizontalScale, verticalScale, 1), 0.35, 1);
};

const ParallelConnector: FC<{
    count: number;
    endsAtHandle?: boolean;
    expansion?: AlertFlowExpansionConfig;
    merge?: boolean;
}> = ({ count, endsAtHandle = true, expansion, merge = false }) => {
    const { token } = useAntDesignTheme();
    const [hovered, setHovered] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const connectorColor = `color-mix(in srgb, ${token.colorTextTertiary} 48%, ${token.colorBorder})`;
    const totalWidth = count === 1 ? 34 : count * parallelNodeWidth + (count - 1) * parallelNodeGap;
    const centers = Array.from({ length: count }, (_, index) =>
        count === 1
            ? 500
            : ((parallelNodeWidth / 2 + index * (parallelNodeWidth + parallelNodeGap)) / totalWidth) * 1000,
    );
    const center = 500;
    const cornerRadius = 9;
    const branchY = connectorHeight / 2;
    const endY = endsAtHandle ? connectorHeight - 9 : connectorHeight - 1;
    const arrowStartY = endY - 5;

    const pathFor = (target: number) => {
        const direction = Math.sign(target - center);

        if (!direction) {
            return `M ${center} 0 V ${endY}`;
        }

        if (merge) {
            return [
                `M ${target} 0`,
                `V ${branchY - cornerRadius}`,
                `Q ${target} ${branchY} ${target - direction * cornerRadius} ${branchY}`,
                `H ${center + direction * cornerRadius}`,
                `Q ${center} ${branchY} ${center} ${branchY + cornerRadius}`,
                `V ${endY}`,
            ].join(' ');
        }

        return [
            `M ${center} 0`,
            `V ${branchY - cornerRadius}`,
            `Q ${center} ${branchY} ${center + direction * cornerRadius} ${branchY}`,
            `H ${target - direction * cornerRadius}`,
            `Q ${target} ${branchY} ${target} ${branchY + cornerRadius}`,
            `V ${endY}`,
        ].join(' ');
    };

    const expansionItems: MenuProps['items'] = expansion
        ? [
              {
                  disabled: true,
                  key: 'workflow-menu-label',
                  label: (
                      <small style={{ color: token.colorTextTertiary }}>
                          {expansion.menuLabel ?? 'Add Workflow Step'}
                      </small>
                  ),
              },
              { type: 'divider' },
              ...expansion.options.map((option) => ({
                  key: option.key,
                  label: (
                      <div
                          style={{
                              alignItems: 'flex-start',
                              display: 'flex',
                              gap: '0.625rem',
                              maxWidth: '26rem',
                              minWidth: '22rem',
                          }}
                      >
                          <PreferenceModalIconAvatar icon={option.icon} size='small' />
                          <div style={{ minWidth: 0 }}>
                              <div
                                  style={{
                                      alignItems: 'center',
                                      display: 'flex',
                                      flexWrap: 'nowrap',
                                      gap: '0.375rem',
                                      whiteSpace: 'nowrap',
                                  }}
                              >
                                  <span>{option.label}</span>
                                  {option.requiresConfiguration ? (
                                      <SmallTag
                                          compact
                                          message='Requires Additional Configuration'
                                          styles={{
                                              backgroundColor: token.colorWarningBg,
                                              border: `1px solid ${token.colorWarningBorder}`,
                                              color: token.colorWarningText,
                                              marginLeft: 0,
                                              whiteSpace: 'nowrap',
                                          }}
                                      />
                                  ) : null}
                              </div>
                              <small
                                  style={{
                                      color: token.colorTextTertiary,
                                      display: 'block',
                                      lineHeight: 1.35,
                                      marginTop: '0.125rem',
                                      whiteSpace: 'normal',
                                  }}
                              >
                                  {option.description}
                              </small>
                          </div>
                      </div>
                  ),
                  onClick: option.onSelect,
              })),
          ]
        : [];

    return (
        <div
            style={{ height: connectorHeight, position: 'relative', width: '100%' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <svg
                aria-hidden
                height={connectorHeight}
                preserveAspectRatio='none'
                style={{ display: 'block', overflow: 'visible', width: '100%' }}
                viewBox={`0 0 1000 ${connectorHeight}`}
            >
                <path
                    d={centers.map(pathFor).join(' ')}
                    fill='none'
                    stroke={connectorColor}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='1.25'
                    vectorEffect='non-scaling-stroke'
                />
                {(merge ? [center] : centers).map((target) => (
                    <polyline
                        key={target}
                        fill='none'
                        points={`${target - 5},${arrowStartY} ${target},${endY} ${target + 5},${arrowStartY}`}
                        stroke={connectorColor}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='1.25'
                        vectorEffect='non-scaling-stroke'
                    />
                ))}
            </svg>
            {expansion?.options.length ? (
                <Dropdown
                    menu={{ items: expansionItems }}
                    open={menuOpen}
                    placement='bottom'
                    trigger={['click']}
                    onOpenChange={setMenuOpen}
                >
                    <Button
                        aria-label={expansion.ariaLabel}
                        icon={<TbPlus />}
                        shape='circle'
                        size='small'
                        style={{
                            backgroundColor: token.colorBgContainer,
                            borderColor: token.colorBorder,
                            boxShadow: token.boxShadowTertiary,
                            left: '50%',
                            opacity: hovered || menuOpen ? 1 : 0,
                            pointerEvents: hovered || menuOpen ? 'auto' : 'none',
                            position: 'absolute',
                            top: '50%',
                            transform: `translate(-50%, -50%) scale(${hovered || menuOpen ? 1 : 0.85})`,
                            transition: 'opacity 160ms ease, transform 160ms ease',
                            zIndex: 4,
                        }}
                        onFocus={() => setHovered(true)}
                    />
                </Dropdown>
            ) : null}
        </div>
    );
};

const ParallelFlowGroup: FC<ParallelFlowGroupProps> = ({ entryExpansion, mergesIntoHandle = true, nodes }) => {
    const count = Math.max(nodes.length, 1);
    const width = count === 1 ? '34rem' : `${count * parallelNodeWidth + (count - 1) * parallelNodeGap}rem`;

    return (
        <div style={{ minWidth: '20rem', width }}>
            <ParallelConnector count={count} expansion={entryExpansion} />
            <div
                style={{
                    display: 'grid',
                    gap: `${parallelNodeGap}rem`,
                    gridTemplateColumns: `repeat(${count}, minmax(${parallelNodeWidth}rem, 1fr))`,
                    width: '100%',
                }}
            >
                {nodes.map((node, index) => (
                    <div key={index}>{node}</div>
                ))}
            </div>
            <ParallelConnector count={count} endsAtHandle={mergesIntoHandle} merge />
        </div>
    );
};

const FlowNode: FC<FlowNodeProps> = ({
    active = false,
    detail,
    onSelect,
    onSelectRequiredField,
    removeAction,
    selected = false,
    status,
    width = 'min(34rem, 100%)',
}) => {
    const { token } = useAntDesignTheme();
    const [hovered, setHovered] = useState(false);
    const completed = status === 'Completed';
    const processing = status === 'Running' || status === 'Scheduled';
    const paused = status === 'Paused';
    const statusStyles: CSSProperties = completed
        ? {
              backgroundColor: token.colorSuccessBg,
              border: `1px solid ${token.colorSuccessBorder}`,
              color: token.colorSuccessText,
          }
        : processing
          ? {
                backgroundColor: token.colorPrimaryBg,
                border: `1px solid ${token.colorPrimaryBorder}`,
                color: token.colorPrimaryText,
            }
          : paused
            ? {
                  backgroundColor: token.colorWarningBg,
                  border: `1px solid ${token.colorWarningBorder}`,
                  color: token.colorWarningText,
              }
            : {
                  backgroundColor: token.colorFillSecondary,
                  border: `1px solid ${token.colorBorder}`,
                  color: token.colorTextSecondary,
              };

    const removeButton = removeAction ? (
        <Button
            aria-label={removeAction.ariaLabel}
            danger
            disabled={removeAction.disabled}
            icon={<TbX />}
            shape='circle'
            size='small'
            style={{
                opacity: hovered ? 1 : 0,
                pointerEvents: hovered ? 'auto' : 'none',
                transform: `scale(${hovered ? 1 : 0.85})`,
                transition: 'opacity 160ms ease, transform 160ms ease',
            }}
            onClick={(event) => event.stopPropagation()}
            onFocus={() => setHovered(true)}
        />
    ) : null;

    const node = (
        <DiagramNodeSurface
            active={active}
            ariaLabel={`${detail.label}: ${detail.title}`}
            selected={selected}
            style={{
                minHeight: '7.5rem',
                padding: `0.875rem ${removeAction ? '3rem' : '1rem'} 0.875rem 1rem`,
            }}
            onSelect={onSelect}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {detail.requiredFields.length && onSelectRequiredField ? (
                <div
                    style={{
                        position: 'absolute',
                        right: removeAction ? '2.75rem' : '0.75rem',
                        top: '-0.625rem',
                        zIndex: 5,
                    }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <AlertRequiredFieldsPreviewPopover
                        fields={detail.requiredFields}
                        onSelect={onSelectRequiredField}
                    />
                </div>
            ) : null}
            {removeAction && removeButton ? (
                <div style={{ position: 'absolute', right: '0.5rem', top: '0.5rem', zIndex: 5 }}>
                    {removeAction.disabled ? (
                        <SmallTooltip title={removeAction.disabledReason}>
                            <span style={{ display: 'inline-flex' }}>{removeButton}</span>
                        </SmallTooltip>
                    ) : (
                        <Popconfirm
                            cancelText='Cancel'
                            description={removeAction.confirmDescription}
                            okButtonProps={{ danger: true }}
                            okText='Remove'
                            title={removeAction.confirmTitle}
                            onConfirm={(event) => {
                                event?.stopPropagation();
                                removeAction.onRemove();
                            }}
                        >
                            {removeButton}
                        </Popconfirm>
                    )}
                </div>
            ) : null}
            <Space align='start' size={12} style={{ width: '100%' }}>
                <PreferenceModalIconAvatar icon={<AlertFlowNodeIcon kind={detail.kind} />} />
                <div style={{ minWidth: 0 }}>
                    <small style={{ color: token.colorTextTertiary }}>{detail.label}</small>
                    <Typography.Title level={5} style={{ margin: '0.125rem 0 0.25rem' }}>
                        {detail.title}
                    </Typography.Title>
                    <div style={{ color: token.colorTextSecondary, lineHeight: 1.45 }}>{detail.description}</div>
                </div>
            </Space>
        </DiagramNodeSurface>
    );

    const nodeWrapper = <div style={{ maxWidth: width === '100%' ? undefined : '34rem', width }}>{node}</div>;

    if (!status) {
        return nodeWrapper;
    }

    return (
        <Badge
            count={
                <span
                    style={{
                        alignItems: 'center',
                        color: statusStyles.color,
                        display: 'inline-flex',
                        gap: '0.25rem',
                        lineHeight: 1,
                    }}
                >
                    {statusIcons[status]}
                    {status}
                </span>
            }
            offset={[-28, 2]}
            style={{
                backgroundColor: statusStyles.backgroundColor,
                border: statusStyles.border,
                borderRadius: token.borderRadiusSM,
                boxShadow: 'none',
                color: statusStyles.color,
                fontSize: token.fontSizeSM,
                fontWeight: 400,
                height: '1.25rem',
                lineHeight: '1.325rem',
                paddingInline: '0.5rem',
            }}
            styles={{ root: { display: 'block', maxWidth: width === '100%' ? undefined : '34rem', width } }}
        >
            {nodeWrapper}
        </Badge>
    );
};

const AlertFlowPreview: FC<AlertFlowPreviewProps> = ({
    categoryLabel,
    conditionLabels,
    conditionExpansion,
    conditionRequiredFields = [],
    deliveryLabels,
    deliveryExpansion,
    deliveryRequiredFields = [],
    description,
    inspectorOpen = true,
    match,
    missingRequiredFields = [],
    name,
    onRemoveCondition,
    onRemoveDelivery,
    onInspectorOpenChange,
    onNodeSelect,
    onSelectRequiredField,
    runtime,
    schedule,
    selectedNodeId,
}) => {
    const { token } = useAntDesignTheme();
    const viewportRef = useRef<HTMLDivElement>(null);
    const graphRef = useRef<HTMLDivElement>(null);
    const [zoom, setZoom] = useState(1);
    const [internalSelectedNodeId, setInternalSelectedNodeId] = useState('alert');
    const lastReportedSelectedNodeSignatureRef = useRef<string>();
    const [viewportSize, setViewportSize] = useState({ height: 0, width: 0 });
    const [graphSize, setGraphSize] = useState({ height: 0, width: 0 });
    const missingRequiredFieldSet = useMemo(() => new Set(missingRequiredFields), [missingRequiredFields]);
    const triggerSignatures = useMemo(
        () => (conditionLabels.length ? conditionLabels : ['No signature conditions configured']),
        [conditionLabels],
    );
    const deliveryDestinations = useMemo(
        () => (deliveryLabels.length ? deliveryLabels : ['No delivery destinations configured']),
        [deliveryLabels],
    );
    const evaluationCount = runtime?.evaluationCount ?? 0;
    const isPaused = runtime?.currentStep === 'paused';
    const alertStatus: FlowStatus | undefined = runtime
        ? isPaused
            ? 'Paused'
            : evaluationCount
              ? 'Completed'
              : 'Configured'
        : undefined;
    const evaluationStatus: FlowStatus | undefined = runtime
        ? isPaused
            ? 'Paused'
            : runtime.currentStep === 'evaluation'
              ? 'Running'
              : evaluationCount
                ? 'Completed'
                : 'Ready'
        : undefined;
    const scheduleStatus: FlowStatus | undefined = runtime ? (isPaused ? 'Paused' : 'Scheduled') : undefined;
    const deliveryStatus: FlowStatus | undefined = runtime
        ? isPaused
            ? 'Paused'
            : runtime.currentStep === 'delivery'
              ? 'Running'
              : evaluationCount
                ? 'Completed'
                : 'Waiting'
        : undefined;

    const nodeDetails = useMemo<AlertFlowNodeDetail[]>(() => {
        const alertDetail: AlertFlowNodeDetail = {
            description: description || `Monitor governed ${categoryLabel.toLowerCase()} activity in this workspace.`,
            id: 'alert',
            kind: 'alert',
            label: 'Alert',
            metadata: [
                { label: 'Category', value: categoryLabel },
                { label: 'State', value: runtime ? runtime.currentStep : 'Configuration' },
            ],
            requiredFields: missingRequiredFieldSet.has('name') ? ['name'] : [],
            title: name || 'Untitled Alert',
        };
        const conditionDetails = triggerSignatures.map<AlertFlowNodeDetail>((signature, index) => ({
            description: `${match === 'all' ? 'Match all' : 'Match any'} · Condition ${index + 1} of ${triggerSignatures.length}`,
            id: `condition-${index}`,
            kind: 'condition',
            label: index === 0 ? 'Trigger Signature' : `Trigger Signature ${index + 1}`,
            metadata: [
                { label: 'Matching Pattern', value: match === 'all' ? 'Match all' : 'Match any' },
                { label: 'Position', value: `${index + 1} of ${triggerSignatures.length}` },
            ],
            requiredFields: conditionRequiredFields[index] ?? [],
            title: signature,
        }));
        const scheduleDetail: AlertFlowNodeDetail = {
            description: `${schedule.timeOfDay} · ${schedule.timezone}`,
            id: 'schedule',
            kind: 'schedule',
            label: 'Evaluation Cadence',
            metadata: [
                { label: 'Frequency', value: frequencyLabels[schedule.frequency] },
                { label: 'Time', value: schedule.timeOfDay },
                { label: 'Timezone', value: schedule.timezone },
            ],
            requiredFields: missingRequiredFieldSet.has('schedule') ? ['schedule'] : [],
            title: frequencyLabels[schedule.frequency],
        };
        const deliveryDetails = deliveryDestinations.map<AlertFlowNodeDetail>((destination, index) => ({
            description: `Delivery method ${index + 1} of ${deliveryDestinations.length}`,
            id: `delivery-${index}`,
            kind: 'delivery',
            label: index === 0 ? 'Delivery' : `Delivery ${index + 1}`,
            metadata: [
                { label: 'Destination', value: destination },
                { label: 'Position', value: `${index + 1} of ${deliveryDestinations.length}` },
            ],
            requiredFields: deliveryRequiredFields[index] ?? [],
            title: destination,
        }));

        return [alertDetail, ...conditionDetails, scheduleDetail, ...deliveryDetails];
    }, [
        categoryLabel,
        conditionRequiredFields,
        deliveryDestinations,
        deliveryRequiredFields,
        description,
        match,
        missingRequiredFieldSet,
        name,
        runtime,
        schedule,
        triggerSignatures,
    ]);

    const nodeDetailsById = useMemo(() => new Map(nodeDetails.map((node) => [node.id, node])), [nodeDetails]);
    const requestedSelectedId = selectedNodeId ?? internalSelectedNodeId;
    const selectedId = nodeDetailsById.has(requestedSelectedId) ? requestedSelectedId : 'alert';

    const selectNode = (node: AlertFlowNodeDetail) => {
        if (selectedNodeId === undefined) {
            setInternalSelectedNodeId(node.id);
        }
        onNodeSelect?.(node);
    };

    useEffect(() => {
        const selectedNode = nodeDetailsById.get(selectedId);
        if (selectedNode) {
            const selectedNodeSignature = JSON.stringify(selectedNode);
            if (lastReportedSelectedNodeSignatureRef.current !== selectedNodeSignature) {
                lastReportedSelectedNodeSignatureRef.current = selectedNodeSignature;
                onNodeSelect?.(selectedNode);
            }
            return;
        }

        const fallback = nodeDetailsById.get('alert');
        if (fallback) {
            const fallbackSignature = JSON.stringify(fallback);
            if (lastReportedSelectedNodeSignatureRef.current !== fallbackSignature) {
                lastReportedSelectedNodeSignatureRef.current = fallbackSignature;
                onNodeSelect?.(fallback);
            }
        }
    }, [nodeDetailsById, onNodeSelect, selectedId]);

    useLayoutEffect(() => {
        const viewport = viewportRef.current;
        const graph = graphRef.current;
        if (!viewport || !graph) {
            return;
        }

        const measure = () => {
            setViewportSize({ height: viewport.clientHeight, width: viewport.clientWidth });
            setGraphSize({ height: graph.offsetHeight, width: graph.offsetWidth });
        };

        measure();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }

        const observer = new ResizeObserver(measure);
        observer.observe(viewport);
        observer.observe(graph);

        return () => observer.disconnect();
    }, [conditionLabels.length, deliveryLabels.length, runtime]);

    const fitScale = calculateAlertFlowFitScale({
        contentHeight: graphSize.height,
        contentWidth: graphSize.width,
        viewportHeight: viewportSize.height,
        viewportWidth: viewportSize.width,
    });
    const effectiveScale = clamp(fitScale * zoom, 0.3, 1.4);
    const frameWidth = Math.max(viewportSize.width, graphSize.width * effectiveScale + graphInset * 2);
    const frameHeight = Math.max(viewportSize.height, graphSize.height * effectiveScale + graphInset * 2);
    const graphLeft = graphSize.width
        ? Math.max(graphInset, (frameWidth - graphSize.width * effectiveScale) / 2)
        : graphInset;

    const detailFor = (id: string) => nodeDetailsById.get(id) as AlertFlowNodeDetail;

    return (
        <DiagramViewport>
            <DiagramCanvasSurface ref={viewportRef}>
                <div
                    style={{
                        height: frameHeight || '100%',
                        minHeight: '100%',
                        minWidth: '100%',
                        position: 'relative',
                        width: frameWidth || '100%',
                    }}
                >
                    <div
                        ref={graphRef}
                        aria-label='Alert workflow preview'
                        role='img'
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            left: graphLeft,
                            minHeight: 'fit-content',
                            position: 'absolute',
                            top: graphInset,
                            transform: `scale(${effectiveScale})`,
                            transformOrigin: 'left top',
                            width: `${Math.max(
                                34,
                                triggerSignatures.length * parallelNodeWidth +
                                    Math.max(0, triggerSignatures.length - 1) * parallelNodeGap,
                                deliveryDestinations.length * parallelNodeWidth +
                                    Math.max(0, deliveryDestinations.length - 1) * parallelNodeGap,
                            )}rem`,
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: token.colorBgContainer,
                                border: `1px solid ${token.colorBorder}`,
                                borderRadius: 999,
                                display: 'inline-flex',
                                padding: '0.5rem 1rem',
                            }}
                        >
                            <span>Start</span>
                        </div>
                        <div style={{ width: '34rem' }}>
                            <ParallelConnector count={1} />
                        </div>
                        <FlowNode
                            detail={detailFor('alert')}
                            selected={selectedId === 'alert'}
                            status={alertStatus}
                            onSelect={() => selectNode(detailFor('alert'))}
                            onSelectRequiredField={onSelectRequiredField}
                        />
                        <ParallelFlowGroup
                            entryExpansion={!runtime ? conditionExpansion : undefined}
                            nodes={triggerSignatures.map((signature, index) => {
                                const detail = detailFor(`condition-${index}`);
                                const canRemove = conditionLabels.length > 1;

                                return (
                                    <FlowNode
                                        key={`${signature}-${index}`}
                                        active={runtime?.currentStep === 'evaluation' && index === 0}
                                        detail={detail}
                                        selected={selectedId === detail.id}
                                        removeAction={
                                            !runtime && conditionLabels.length && onRemoveCondition
                                                ? {
                                                      ariaLabel: `Remove trigger condition ${index + 1}`,
                                                      confirmDescription:
                                                          'This condition will be removed from the alert signature.',
                                                      confirmTitle: 'Remove trigger condition?',
                                                      disabled: !canRemove,
                                                      disabledReason: 'At least one trigger condition is required.',
                                                      onRemove: () => onRemoveCondition(index),
                                                  }
                                                : undefined
                                        }
                                        status={
                                            evaluationStatus === 'Running' && index > 0 ? 'Ready' : evaluationStatus
                                        }
                                        width='100%'
                                        onSelect={() => selectNode(detail)}
                                        onSelectRequiredField={onSelectRequiredField}
                                    />
                                );
                            })}
                        />
                        <FlowNode
                            active={runtime?.currentStep === 'scheduled'}
                            detail={detailFor('schedule')}
                            selected={selectedId === 'schedule'}
                            status={scheduleStatus}
                            onSelect={() => selectNode(detailFor('schedule'))}
                            onSelectRequiredField={onSelectRequiredField}
                        />
                        <ParallelFlowGroup
                            entryExpansion={!runtime ? deliveryExpansion : undefined}
                            mergesIntoHandle={false}
                            nodes={deliveryDestinations.map((destination, index) => {
                                const detail = detailFor(`delivery-${index}`);
                                const canRemove = deliveryLabels.length > 1;

                                return (
                                    <FlowNode
                                        key={`${destination}-${index}`}
                                        active={runtime?.currentStep === 'delivery' && index === 0}
                                        detail={detail}
                                        selected={selectedId === detail.id}
                                        removeAction={
                                            !runtime && deliveryLabels.length && onRemoveDelivery
                                                ? {
                                                      ariaLabel: `Remove delivery method ${index + 1}`,
                                                      confirmDescription:
                                                          'This destination will no longer receive the alert.',
                                                      confirmTitle: 'Remove delivery method?',
                                                      disabled: !canRemove,
                                                      disabledReason: 'At least one delivery method is required.',
                                                      onRemove: () => onRemoveDelivery(index),
                                                  }
                                                : undefined
                                        }
                                        status={deliveryStatus === 'Running' && index > 0 ? 'Waiting' : deliveryStatus}
                                        width='100%'
                                        onSelect={() => selectNode(detail)}
                                        onSelectRequiredField={onSelectRequiredField}
                                    />
                                );
                            })}
                        />
                        <div
                            style={{
                                alignItems: 'center',
                                backgroundColor: token.colorBgContainer,
                                border: `1px solid ${token.colorBorder}`,
                                borderRadius: 999,
                                display: 'inline-flex',
                                gap: '0.375rem',
                                padding: '0.5rem 1rem',
                            }}
                        >
                            <span>{runtime ? (isPaused ? 'Alert Paused' : 'Alert Ready') : 'End'}</span>
                        </div>
                    </div>
                </div>
            </DiagramCanvasSurface>
            {runtime ? (
                <Space
                    size={8}
                    style={{
                        backgroundColor: token.colorBgContainer,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        left: '1rem',
                        padding: '0.25rem 0.5rem',
                        pointerEvents: 'auto',
                        position: 'absolute',
                        top: '1rem',
                        zIndex: 5,
                    }}
                    wrap
                >
                    <small style={{ color: token.colorTextTertiary }}>Current step: {runtime.currentStep}</small>
                    <small style={{ color: token.colorTextTertiary }}>
                        {evaluationCount.toLocaleString()} evaluations
                    </small>
                </Space>
            ) : null}
            <DiagramZoomControls
                inspectorLabel='workflow details'
                inspectorOpen={inspectorOpen}
                zoom={zoom}
                onInspectorOpenChange={onInspectorOpenChange}
                onZoomChange={setZoom}
            />
        </DiagramViewport>
    );
};

export type { AlertFlowExpansionOption, AlertFlowFitInput };
export default AlertFlowPreview;
